import { BrowserRouter as Router } from "react-router-dom";
import { PageTitleSync } from '@/components/shared/PageTitle/PageTitleSync';
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import AppRoutes from "./router/AppRoutes";
import { NotificationProvider } from "./contexts/admin/NotificationContext";
import { AuthProvider, useAuth } from "./contexts/shared/AuthContext";
import { BrandingProvider } from "./contexts/shared/BrandingContext";
import { ThemeProvider } from "./contexts/shared/ThemeContext";
import { ErrorBoundary } from "./components/shared/ErrorBoundary/ErrorBoundary";
import { LanguageProvider } from "./i18n";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { retry: 1, staleTime: 30_000 },
  },
});

/**
 * Inner wrapper so we can read auth context (superadmin + tenantId)
 * before constructing the LanguageProvider.
 */
const LanguageAwareApp = () => {
  const { user, isLoading } = useAuth();

  // Collect all permission names the user holds across all their roles
  const userPermissions = new Set<string>(
    user?.roles?.flatMap((r: any) => (r.permissions ?? []).map((p: any) => p.name ?? p)) ?? []
  );

  const isSuperAdmin = user?.roles?.some((r: any) => {
    const name = (r.name || '').toLowerCase();
    return name === 'superadmin' || name === 'system:superadmin' || name.includes('superadmin') || name === 'site_admin';
  }) ?? false;

  // Can the user enter content in multiple languages (EN/AR/FR tabs on data-entry fields)?
  // Granted to: site_admin (ALL), business_admin, tenant_admin, editing_trainer
  const canWriteMultilang = isSuperAdmin || userPermissions.has('content:multilang_write');

  // Can the user activate ?showlang=true to edit UI string keys?
  // Granted to: site_admin (ALL), business_admin
  const hasShowLangPermission = isSuperAdmin || userPermissions.has('lang:showlang');

  const tenantId = (user as any)?.tenant_id ?? null;

  if (isLoading) return null; // Wait for auth before setting up i18n

  return (
    <LanguageProvider tenantId={tenantId} isSuperAdmin={isSuperAdmin} hasShowLangPermission={hasShowLangPermission} canWriteMultilang={canWriteMultilang}>
      <BrandingProvider>
        <ThemeProvider>
          <AppRoutes />
        </ThemeProvider>
      </BrandingProvider>
    </LanguageProvider>
  );
};

const App = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <Router>
        <PageTitleSync />
        <AuthProvider>
          <NotificationProvider>
            <ErrorBoundary>
              <LanguageAwareApp />
            </ErrorBoundary>
          </NotificationProvider>
        </AuthProvider>
      </Router>
    </QueryClientProvider>
  );
};

export default App;